import React, { useEffect, useRef, useState } from "react";
import { numberlikeEditorText, parseNumberlike } from "@/utils/numberlike";

// The editor for a `number` attribute (#10991). A text box, because a
// number cell accepts any text that contains a number: "the fee is 1.25%,
// 1.375% or 1.5% depending on NAV tier" is a legitimate fee. The first run of
// numbers is what the cell sorts and filters by, and the words around it are
// the note.
//
// A plain number looks like a plain number: no tint, nothing under the box.
// Once there are words beside the numbers, the run is tinted in place so the
// boundary is visible. Text with no number at all reads "Include a number" in
// the danger tone -- once the person leaves the box or presses Enter, never
// mid-keystroke -- and is never committed, so the caller's save has nothing
// to write. The server derives the stored parts from the text; the mirror
// here is for the highlight only.

export const NUMBERLIKE_REFUSAL = "Include a number";

interface NumberlikeInputProps {
  // The stored number (with its range end and note), the text typed so far,
  // or null for an empty cell.
  value: number | string | null | undefined;
  end?: number | null;
  note?: string | null;
  unit?: string | null;
  // Called with the typed text when it starts with a number, and with null
  // when the box is cleared. Never called for text the grammar refuses.
  onChange: (text: string | null) => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  compact?: boolean;
}

const initialText = (
  value: number | string | null | undefined,
  end: number | null | undefined,
  note: string | null | undefined,
  unit: string | null | undefined
): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return numberlikeEditorText(value, end, note, unit);
};

const NumberlikeInput: React.FC<NumberlikeInputProps> = ({
  value,
  end,
  note,
  unit,
  onChange,
  placeholder,
  className = "",
  inputRef,
  compact = false,
}) => {
  const [text, setText] = useState<string>(() =>
    initialText(value, end, note, unit)
  );
  const [isFocused, setIsFocused] = useState(false);
  // The refusal waits for a pause: leaving the box or pressing Enter. Typing
  // "the fee is" on the way to the number must not be nagged.
  const [settled, setSettled] = useState(false);
  // The tint layer mirrors the input's text and has to follow its horizontal
  // scroll, or a long value leaves the tint sitting over the wrong letters.
  const ownRef = useRef<HTMLInputElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const syncScroll = () => {
    if (mirrorRef.current && ownRef.current) {
      mirrorRef.current.scrollLeft = ownRef.current.scrollLeft;
    }
  };
  useEffect(syncScroll);

  // Sync from the outside when the parent's value changes and this box is
  // not the source of the change (the PercentInput rule): an autofill that
  // lands while the field is open must not overwrite what is being typed.
  // Keyed on the outside value alone, not on focus: a blur with refused text
  // in the box must leave that text standing beside its message, not snap
  // the box back to the value the parent still holds.
  const outside = initialText(value, end, note, unit);
  const lastOutside = useRef(outside);
  useEffect(() => {
    if (outside === lastOutside.current) return;
    lastOutside.current = outside;
    if (isFocused) return;
    setText(outside);
  }, [outside, isFocused]);

  const parsed = text.trim() === "" ? null : parseNumberlike(text, unit);
  const unreadable = text.trim() !== "" && parsed === null;
  const refused = unreadable && settled;
  // The boundary is worth showing only when there are words beside the run.
  const hasNote = parsed !== null && parsed.note !== null;
  const runStart = hasNote ? parsed.runStart : 0;
  const runEnd = hasNote ? parsed.runEnd : 0;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    setSettled(false);
    if (next.trim() === "") {
      onChange(null);
      return;
    }
    if (parseNumberlike(next, unit) !== null) {
      onChange(next);
    }
  };

  // The tint sits behind the input in a mirror layer with the same box, the
  // same font and the same padding, so its run lines up under the typed
  // characters. Its own text is transparent: only the tint shows, and the
  // input above draws the ink.
  const boxClasses = compact
    ? "px-2 py-1 text-sm rounded-none"
    : "px-3 py-2 text-sm rounded-md";

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div
          ref={mirrorRef}
          aria-hidden
          className={`absolute inset-0 border border-transparent whitespace-pre overflow-hidden pointer-events-none text-transparent ${boxClasses}`}
        >
          <span>{text.slice(0, runStart)}</span>
          <span
            data-testid="numberlike-run"
            className="rounded-sm"
            style={{
              backgroundColor:
                runEnd > runStart ? "var(--fp-accent-soft)" : "transparent",
            }}
          >
            {text.slice(runStart, runEnd)}
          </span>
          <span>{text.slice(runEnd)}</span>
        </div>
        <input
          ref={(node) => {
            ownRef.current = node;
            if (inputRef) inputRef.current = node;
          }}
          type="text"
          inputMode="text"
          value={text}
          onChange={handleInputChange}
          onScroll={syncScroll}
          onKeyUp={syncScroll}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSettled(true);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            setSettled(true);
          }}
          placeholder={placeholder}
          aria-invalid={refused || undefined}
          className={`relative w-full bg-transparent border border-gray-300 focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] ${boxClasses} ${className}`}
        />
      </div>
      {refused && (
        <div
          role="alert"
          className="mt-0.5 text-xs leading-4 truncate"
          style={{ color: "var(--fp-danger)" }}
        >
          {NUMBERLIKE_REFUSAL}
        </div>
      )}
    </div>
  );
};

export default NumberlikeInput;
